import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { formatINR, formatDate, statusColor } from "../utils/format";
import { ArrowLeft, Target, Bell, CheckSquare } from "lucide-react";

export default function AccountWorkspace() {
  const { id } = useParams();
  const [ws, setWs] = useState(null);

  useEffect(() => { api.get(`/clients/account/${id}/workspace`).then((r) => setWs(r.data)).catch(() => {}); }, [id]);

  if (!ws) return <Layout title="Loading…"><div className="text-sm text-muted-foreground p-6">Loading account…</div></Layout>;

  return (
    <Layout title={ws.name} subtitle={`${ws.google_customer_id} · ${ws.currency || "—"}`}>
      <Link to="/clients" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-clients">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Clients
      </Link>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-card border border-border rounded-sm p-3"><div className="label-mono">Client</div><div className="font-medium mt-1">{ws.client?.name || "—"}</div></div>
        <div className="bg-card border border-border rounded-sm p-3"><div className="label-mono">Active Campaigns</div><div className="font-heading text-lg font-light tabular mt-1">{ws.summary.active_campaigns}/{ws.summary.total_campaigns}</div></div>
        <div className="bg-card border border-border rounded-sm p-3"><div className="label-mono">Spend · 7d</div><div className="font-heading text-lg font-light tabular mt-1">{formatINR(ws.summary.spend_7d, { compact: true })}</div></div>
        <div className="bg-card border border-border rounded-sm p-3"><div className="label-mono">Platform</div><div className="font-medium mt-1 capitalize">{ws.platform.replace("_", " ")}</div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-sm p-5">
          <div className="label-mono mb-3 flex items-center gap-1.5"><Target className="w-3.5 h-3.5" /> Campaigns</div>
          {ws.campaigns.map((c) => (
            <Link key={c.id} to={`/campaigns/${c.id}`} className="flex items-center justify-between py-2 border-b border-border last:border-b-0 hover:bg-secondary/30 -mx-2 px-2 rounded-sm">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{c.name}</div>
                <div className="text-[11px] text-muted-foreground">{c.type} · Daily {formatINR(c.daily_budget)}</div>
              </div>
              <StatusBadge tone={statusColor(c.status)}>{c.status}</StatusBadge>
            </Link>
          ))}
          {ws.campaigns.length === 0 && <div className="text-sm text-muted-foreground">No campaigns.</div>}
        </div>

        <div className="bg-card border border-border rounded-sm p-5">
          <div className="label-mono mb-3 flex items-center gap-1.5"><Bell className="w-3.5 h-3.5" /> Alerts & Tasks</div>
          {ws.alerts.slice(0, 5).map((a) => (
            <div key={a.id} className="py-2 border-b border-border last:border-b-0">
              <div className="flex items-start gap-2">
                <StatusBadge tone={a.severity === "critical" ? "critical" : a.severity === "warning" ? "warning" : "primary"}>{a.severity}</StatusBadge>
                <div className="text-sm flex-1">{a.title}</div>
              </div>
            </div>
          ))}
          <div className="mt-3 pt-3 border-t border-border">
            <div className="label-mono mb-2 flex items-center gap-1.5"><CheckSquare className="w-3.5 h-3.5" /> Open Tasks</div>
            {ws.tasks.filter((t) => t.status !== "done").slice(0, 4).map((t) => (
              <div key={t.id} className="text-sm py-1">{t.title} <span className="text-[11px] text-muted-foreground">· {t.priority}{t.due_date ? ` · due ${formatDate(t.due_date)}` : ""}</span></div>
            ))}
            {ws.tasks.filter((t) => t.status !== "done").length === 0 && <div className="text-xs text-muted-foreground">No open tasks.</div>}
          </div>
        </div>
      </div>
    </Layout>
  );
}
